import React, { useState, useEffect } from 'react';

import API from './config';
const BLEU = '#1E3A5F';
const VIOLET = '#6B21A8';

const SIRET_OBLIGATOIRE_CLE = 'siret_obligatoire_facturation';

export default function Administration({ apiFetch, utilisateurCourant }) {
  const [onglet, setOnglet] = useState('utilisateurs'); // 'utilisateurs' | 'demandes' | 'logs' | 'parametres'
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', mot_de_passe: '', profil: 'gestionnaire' });
  const PROFILS = { admin: { label: '🔑 Admin', color: '#7C3AED' }, gestionnaire: { label: '👤 Gestionnaire', color: '#1E3A5F' }, consultation: { label: '👁 Consultation', color: '#6B7280' } };
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');
  const [loading, setLoading] = useState(false);

  // Demandes reset
  const [demandes, setDemandes] = useState([]);
  const [nouveauMdp, setNouveauMdp] = useState({}); // { [userId]: string }
  const [loadingMdp, setLoadingMdp] = useState({});

  // Logs
  const [logs, setLogs] = useState([]);
  const [filtreLog, setFiltreLog] = useState('');

  // Paramètres
  const [siretObligatoire, setSiretObligatoire] = useState(false);
  const [loadingParametre, setLoadingParametre] = useState(false);

  // CGV
  const [cgvVersions, setCgvVersions] = useState([]);
  const [cgvForm, setCgvForm] = useState(null); // null | { id?, label, contenu }
  const [cgvVoir, setCgvVoir] = useState(null); // version en cours de consultation (lecture seule)
  const [savingCgv, setSavingCgv] = useState(false);

  const charger = async () => {
    const r = await apiFetch(`${API}/auth/utilisateurs`);
    if (r.ok) { setUtilisateurs(await r.json()); setErreur(''); }
    else setErreur(`Impossible de charger les utilisateurs (HTTP ${r.status}). Réessayez ou contactez l'administrateur.`);
  };

  const chargerDemandes = async () => {
    const r = await apiFetch(`${API}/auth/demandes-reset`);
    if (r.ok) { setDemandes(await r.json()); setErreur(''); }
    else setErreur(`Impossible de charger les demandes de reset (HTTP ${r.status}).`);
  };

  const chargerLogs = async () => {
    const r = await apiFetch(`${API}/auth/logs`);
    if (r.ok) { setLogs(await r.json()); setErreur(''); }
    else setErreur(`Impossible de charger les logs de sécurité (HTTP ${r.status}).`);
  };

  const chargerParametres = async () => {
    const r = await apiFetch(`${API}/parametres/${SIRET_OBLIGATOIRE_CLE}`);
    if (r.ok) { const d = await r.json(); setSiretObligatoire(d.valeur === 'true'); setErreur(''); }
    else setErreur(`Impossible de charger les paramètres (HTTP ${r.status}).`);
    chargerCgv();
  };

  const chargerCgv = async () => {
    const r = await apiFetch(`${API}/cgv/versions`);
    if (r.ok) setCgvVersions(await r.json());
  };

  const ouvrirNouvelleCgv = () => setCgvForm({ label: '', contenu: '' });
  const ouvrirEditionCgv = (v) => setCgvForm({ id: v.id, label: v.label, contenu: v.contenu });
  const annulerCgv = () => setCgvForm(null);

  const enregistrerCgv = async () => {
    if (!cgvForm.label.trim() || !cgvForm.contenu.trim()) return alert('Label et contenu sont obligatoires');
    setSavingCgv(true);
    const estEdition = !!cgvForm.id;
    const url = estEdition ? `${API}/cgv/versions/${cgvForm.id}` : `${API}/cgv/versions`;
    const r = await apiFetch(url, { method: estEdition ? 'PUT' : 'POST', body: JSON.stringify({ label: cgvForm.label.trim(), contenu: cgvForm.contenu }) });
    if (r.ok) { await chargerCgv(); setCgvForm(null); showMsg('✅ Version CGV enregistrée'); }
    else { const e = await r.json(); alert(e.detail || 'Erreur'); }
    setSavingCgv(false);
  };

  const activerCgv = async (v) => {
    if (!window.confirm(`Activer « ${v.label} » ? Elle devient la version en vigueur pour tous les nouveaux devis. Une fois activée, elle ne pourra plus être modifiée.`)) return;
    const r = await apiFetch(`${API}/cgv/versions/${v.id}/activer`, { method: 'POST' });
    if (r.ok) { await chargerCgv(); showMsg(`🚀 « ${v.label} » activée`); }
    else { const e = await r.json(); alert(e.detail || 'Erreur'); }
  };

  const supprimerCgv = async (v) => {
    if (!window.confirm(`Supprimer le brouillon « ${v.label} » ?`)) return;
    const r = await apiFetch(`${API}/cgv/versions/${v.id}`, { method: 'DELETE' });
    if (r.ok) { await chargerCgv(); showMsg('🗑️ Brouillon supprimé'); }
    else { const e = await r.json(); alert(e.detail || 'Erreur'); }
  };

  // Chargement au mount uniquement — les fonctions sont stables dans la durée du composant.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, []);
  useEffect(() => {
    if (onglet === 'demandes') chargerDemandes();
    if (onglet === 'logs') chargerLogs();
    if (onglet === 'parametres') chargerParametres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onglet]);

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const creer = async () => {
    if (!form.nom || !form.email || !form.mot_de_passe) return alert('Nom, email et mot de passe sont obligatoires');
    setLoading(true);
    const r = await apiFetch(`${API}/auth/utilisateurs`, { method: 'POST', body: JSON.stringify(form) });
    if (r.ok) { await charger(); setShowForm(false); setForm({ nom: '', prenom: '', email: '', mot_de_passe: '', profil: 'gestionnaire' }); showMsg('✅ Utilisateur créé !'); }
    else { const e = await r.json(); alert(e.detail || 'Erreur'); }
    setLoading(false);
  };

  const toggleActif = async (user) => {
    const r = await apiFetch(`${API}/auth/utilisateurs/${user.id}`, { method: 'PATCH', body: JSON.stringify({ est_actif: !user.est_actif }) });
    if (r.ok) { await charger(); showMsg(user.est_actif ? '⛔ Compte désactivé' : '✅ Compte réactivé'); }
  };

  const changerProfil = async (user, profil) => {
    const r = await apiFetch(`${API}/auth/utilisateurs/${user.id}`, { method: 'PATCH', body: JSON.stringify({ profil }) });
    if (r.ok) { await charger(); showMsg('✅ Profil modifié'); }
  };

  const toggleSiretObligatoire = async () => {
    const activer = !siretObligatoire;
    if (activer && !window.confirm(
      "Activer cette bascule empêchera d'émettre une facture à un client professionnel sans SIRET renseigné.\n\n" +
      "À n'activer que lorsque l'obligation légale (facturation électronique) entre réellement en vigueur.\n\nConfirmer l'activation ?"
    )) return;
    setLoadingParametre(true);
    const r = await apiFetch(`${API}/parametres/${SIRET_OBLIGATOIRE_CLE}`, { method: 'PATCH', body: JSON.stringify({ valeur: activer ? 'true' : 'false' }) });
    if (r.ok) { setSiretObligatoire(activer); showMsg(activer ? '🔒 SIRET désormais obligatoire pour facturer un pro' : '🔓 SIRET redevenu non-bloquant'); }
    else { const e = await r.json(); alert(e.detail || 'Erreur'); }
    setLoadingParametre(false);
  };

  const reset2fa = async (user) => {
    if (!window.confirm(`Réinitialiser le 2FA de ${user.nom} ? Il devra le reconfigurer à la prochaine connexion.`)) return;
    const r = await apiFetch(`${API}/auth/utilisateurs/${user.id}/reset-2fa`, { method: 'POST' });
    if (r.ok) showMsg('✅ 2FA réinitialisé');
  };

  const traiterDemande = async (userId, userNom) => {
    const mdp = nouveauMdp[userId];
    if (!mdp || mdp.length < 6) return alert('Saisissez un nouveau mot de passe (6 caractères minimum)');
    if (!window.confirm(`Définir un nouveau mot de passe pour ${userNom} ?`)) return;
    setLoadingMdp(prev => ({ ...prev, [userId]: true }));
    const r = await apiFetch(`${API}/auth/utilisateurs/${userId}/changer-mot-de-passe`, {
      method: 'POST', body: JSON.stringify({ nouveau_mot_de_passe: mdp })
    });
    if (r.ok) {
      setNouveauMdp(prev => ({ ...prev, [userId]: '' }));
      await chargerDemandes();
      showMsg('✅ Mot de passe modifié, demande traitée');
    } else {
      const e = await r.json(); alert(e.detail || 'Erreur');
    }
    setLoadingMdp(prev => ({ ...prev, [userId]: false }));
  };

  const LABELS_ACTION = {
    login_ok: { label: 'Connexion réussie', color: '#16A34A', bg: '#DCFCE7' },
    login_echec: { label: 'Échec connexion', color: '#DC2626', bg: '#FEF2F2' },
    login_echec_desactive: { label: 'Connexion — compte désactivé', color: '#DC2626', bg: '#FEF2F2' },
    '2fa_echec': { label: 'Code 2FA incorrect', color: '#EA580C', bg: '#FFF7ED' },
    reset_mdp_demande: { label: 'Demande reset mot de passe', color: '#D97706', bg: '#FFFBEB' },
    reset_mdp_effectue: { label: 'Mot de passe réinitialisé', color: '#0369A1', bg: '#E0F2FE' },
    reset_2fa: { label: '2FA réinitialisé (admin)', color: '#7C3AED', bg: '#F5F3FF' },
    changement_profil: { label: 'Profil modifié', color: '#6B21A8', bg: '#F5F3FF' },
    compte_desactive: { label: 'Compte désactivé', color: '#DC2626', bg: '#FEF2F2' },
    compte_reactive: { label: 'Compte réactivé', color: '#16A34A', bg: '#DCFCE7' },
    creation_utilisateur: { label: 'Utilisateur créé', color: '#0369A1', bg: '#E0F2FE' },
    changement_mdp_perso: { label: 'Mot de passe changé (utilisateur)', color: '#0369A1', bg: '#E0F2FE' },
    client_cree: { label: 'Client créé', color: '#0369A1', bg: '#E0F2FE' },
    client_modifie: { label: 'Client modifié', color: '#1E3A5F', bg: '#E0E7FF' },
    client_supprime: { label: 'Client supprimé', color: '#DC2626', bg: '#FEF2F2' },
    devis_cree: { label: 'Devis créé', color: '#0369A1', bg: '#E0F2FE' },
    devis_modifie: { label: 'Devis modifié', color: '#1E3A5F', bg: '#E0E7FF' },
    devis_supprime: { label: 'Devis supprimé', color: '#DC2626', bg: '#FEF2F2' },
    facture_creee: { label: 'Facture créée', color: '#0369A1', bg: '#E0F2FE' },
    facture_emise: { label: 'Facture émise', color: '#16A34A', bg: '#DCFCE7' },
    acompte_cree: { label: 'Acompte créé', color: '#0369A1', bg: '#E0F2FE' },
    paiement_enregistre: { label: 'Paiement enregistré', color: '#16A34A', bg: '#DCFCE7' },
    avoir_cree: { label: 'Avoir créé', color: '#D97706', bg: '#FFFBEB' },
    avoir_rembourse: { label: 'Avoir remboursé', color: '#16A34A', bg: '#DCFCE7' },
    parametre_modifie: { label: 'Paramètre modifié', color: '#6B21A8', bg: '#F5F3FF' },
    achat_cree: { label: 'Achat enregistré', color: '#0369A1', bg: '#E0F2FE' },
    achat_modifie: { label: 'Achat modifié', color: '#1E3A5F', bg: '#E0E7FF' },
    achat_supprime: { label: 'Achat supprimé', color: '#DC2626', bg: '#FEF2F2' },
    cgv_version_creee: { label: 'Version CGV créée', color: '#0369A1', bg: '#E0F2FE' },
    cgv_version_modifiee: { label: 'Version CGV modifiée', color: '#1E3A5F', bg: '#E0E7FF' },
    cgv_version_activee: { label: 'Version CGV activée', color: '#16A34A', bg: '#DCFCE7' },
    cgv_version_supprimee: { label: 'Version CGV supprimée', color: '#DC2626', bg: '#FEF2F2' },
  };

  const logsFiltres = logs.filter(l => {
    if (!filtreLog) return true;
    const q = filtreLog.toLowerCase();
    return (l.action || '').toLowerCase().includes(q)
      || (l.auteur || '').toLowerCase().includes(q)
      || (l.cible || '').toLowerCase().includes(q)
      || (l.details || '').toLowerCase().includes(q);
  });

  const s = {
    card: { background: 'white', borderRadius: '10px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '16px' },
    th: { background: VIOLET, color: 'white', padding: '10px 14px', textAlign: 'left', fontSize: '13px' },
    td: { padding: '10px 14px', borderBottom: '1px solid #F3F4F6', fontSize: '14px' },
    btn: (c) => ({ background: c || BLEU, color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }),
    btnSm: (c) => ({ background: c || '#E5E7EB', color: c ? 'white' : '#374151', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }),
    input: { width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', marginBottom: '10px', boxSizing: 'border-box' },
    label: { display: 'block', fontSize: '13px', color: '#374151', fontWeight: '600', marginBottom: '4px' },
    badge: (actif) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: actif ? '#DCFCE7' : '#F3F4F6', color: actif ? '#16A34A' : '#9CA3AF' }),
    ongletBtn: (actif) => ({
      padding: '8px 20px', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer', fontSize: '14px', fontWeight: actif ? '700' : '500',
      background: actif ? 'white' : '#F3F4F6', color: actif ? VIOLET : '#6B7280',
      borderBottom: actif ? '3px solid ' + VIOLET : '3px solid transparent',
    }),
  };

  return (
    <div>
      {message && <div style={{ position: 'fixed', top: '20px', right: '20px', background: BLEU, color: 'white', padding: '12px 20px', borderRadius: '8px', zIndex: 2000 }}>{message}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <h2 style={{ color: VIOLET, margin: 0, fontSize: '20px' }}>⚙️ Administration — Gestion des accès</h2>
        {onglet === 'utilisateurs' && <button style={s.btn(VIOLET)} onClick={() => setShowForm(!showForm)}>+ Nouvel utilisateur</button>}
      </div>

      {erreur && (
        <div style={{ background: '#FEE2E2', color: '#B91C1C', padding: '10px 14px', borderRadius: '8px', marginTop: '12px', border: '1px solid #FCA5A5', fontSize: '14px' }}>
          ⚠️ {erreur}
        </div>
      )}

      {/* Onglets */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '0', borderBottom: '3px solid #E5E7EB', marginTop: '16px' }}>
        <button style={s.ongletBtn(onglet === 'utilisateurs')} onClick={() => setOnglet('utilisateurs')}>👥 Utilisateurs</button>
        <button style={s.ongletBtn(onglet === 'demandes')} onClick={() => setOnglet('demandes')}>
          🔑 Demandes de reset
          {demandes.filter(d => !d.traitee).length > 0 && (
            <span style={{ marginLeft: '6px', background: '#DC2626', color: 'white', borderRadius: '10px', padding: '1px 7px', fontSize: '11px' }}>
              {demandes.filter(d => !d.traitee).length}
            </span>
          )}
        </button>
        <button style={s.ongletBtn(onglet === 'logs')} onClick={() => setOnglet('logs')}>📋 Logs de sécurité</button>
        <button style={s.ongletBtn(onglet === 'parametres')} onClick={() => setOnglet('parametres')}>⚙️ Paramètres</button>
      </div>

      {/* ─── Onglet Utilisateurs ─── */}
      {onglet === 'utilisateurs' && (
        <>
          {showForm && (
            <div style={{ ...s.card, borderRadius: '0 0 10px 10px', marginBottom: '16px', marginTop: '0' }}>
              <h4 style={{ color: VIOLET, margin: '0 0 16px 0' }}>Créer un utilisateur</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={s.label}>Nom *</label>
                  <input style={s.input} value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Nom" />
                </div>
                <div>
                  <label style={s.label}>Prénom</label>
                  <input style={s.input} value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} placeholder="Prénom" />
                </div>
                <div>
                  <label style={s.label}>Email *</label>
                  <input style={s.input} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@exemple.fr" />
                </div>
                <div>
                  <label style={s.label}>Mot de passe *</label>
                  <input style={s.input} type="password" value={form.mot_de_passe} onChange={e => setForm({ ...form, mot_de_passe: e.target.value })} placeholder="Mot de passe temporaire" />
                </div>
                <div>
                  <label style={s.label}>Profil</label>
                  <select style={s.input} value={form.profil} onChange={e => setForm({ ...form, profil: e.target.value })}>
                    <option value="admin">🔑 Admin — accès total</option>
                    <option value="gestionnaire">👤 Gestionnaire — tout sauf administration</option>
                    <option value="consultation">👁 Consultation — lecture seule</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button style={s.btn(VIOLET)} onClick={creer} disabled={loading}>{loading ? 'Création...' : '✅ Créer'}</button>
                <button style={s.btn('#6B7280')} onClick={() => setShowForm(false)}>Annuler</button>
              </div>
            </div>
          )}

          <div style={{ ...s.card, borderRadius: showForm ? '10px' : '0 10px 10px 10px', marginTop: '0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Nom', 'Email', 'Profil', 'Statut', '2FA', 'Dernière connexion', 'Actions'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {utilisateurs.map((u, i) => (
                  <tr key={u.id} style={{ background: i % 2 === 0 ? 'white' : '#F9FAFB' }}>
                    <td style={{ ...s.td, fontWeight: '600' }}>{u.nom} {u.prenom || ''}</td>
                    <td style={s.td}>{u.email}</td>
                    <td style={s.td}>
                      {u.id !== utilisateurCourant.id
                        ? <select style={{ fontSize:'12px', padding:'3px 6px', border:'1px solid #D1D5DB', borderRadius:'6px', cursor:'pointer' }}
                            value={u.profil || (u.est_admin ? 'admin' : 'gestionnaire')}
                            onChange={e => changerProfil(u, e.target.value)}>
                            <option value="admin">🔑 Admin</option>
                            <option value="gestionnaire">👤 Gestionnaire</option>
                            <option value="consultation">👁 Consultation</option>
                          </select>
                        : <span style={{ fontSize:'13px', color:'#6B7280' }}>{PROFILS[u.profil]?.label || '🔑 Admin'}</span>
                      }
                    </td>
                    <td style={s.td}><span style={s.badge(u.est_actif)}>{u.est_actif ? 'Actif' : 'Désactivé'}</span></td>
                    <td style={s.td}>{u.totp_active ? '✅ Actif' : '⚠️ Non configuré'}</td>
                    <td style={s.td}>{u.last_login ? new Date(u.last_login).toLocaleDateString('fr-FR') : '—'}</td>
                    <td style={s.td}>
                      {u.id !== utilisateurCourant.id && (
                        <button style={s.btnSm(u.est_actif ? '#DC2626' : '#16A34A')} onClick={() => toggleActif(u)}>
                          {u.est_actif ? '⛔ Désactiver' : '✅ Réactiver'}
                        </button>
                      )}
                      {u.totp_active && (
                        <button style={s.btnSm('#F97316')} onClick={() => reset2fa(u)}>🔄 Reset 2FA</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ─── Onglet Demandes de reset ─── */}
      {onglet === 'demandes' && (
        <div style={{ ...s.card, borderRadius: '0 10px 10px 10px', marginTop: '0' }}>
          {demandes.length === 0 ? (
            <p style={{ color: '#6B7280', textAlign: 'center', padding: '32px 0' }}>Aucune demande de réinitialisation</p>
          ) : (
            <>
              <p style={{ color: '#6B7280', fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
                {demandes.filter(d => !d.traitee).length} demande(s) en attente · {demandes.filter(d => d.traitee).length} traitée(s)
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Utilisateur', 'Email', 'Date demande', 'Statut', 'Nouveau mot de passe', 'Action'].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {demandes.map((d, i) => (
                    <tr key={d.id} style={{ background: d.traitee ? '#F9FAFB' : (i % 2 === 0 ? 'white' : '#FFFBEB') }}>
                      <td style={{ ...s.td, fontWeight: d.traitee ? '400' : '600', color: d.traitee ? '#9CA3AF' : 'inherit' }}>
                        {d.nom} {d.prenom || ''}
                      </td>
                      <td style={{ ...s.td, color: d.traitee ? '#9CA3AF' : 'inherit' }}>{d.email}</td>
                      <td style={{ ...s.td, color: '#6B7280', fontSize: '13px' }}>
                        {new Date(d.timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={s.td}>
                        {d.traitee
                          ? <span style={{ ...s.badge(true), background: '#DCFCE7', color: '#16A34A' }}>✅ Traitée</span>
                          : <span style={{ ...s.badge(false), background: '#FEF3C7', color: '#D97706' }}>⏳ En attente</span>
                        }
                      </td>
                      <td style={s.td}>
                        {!d.traitee && (
                          <input
                            style={{ padding: '5px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', width: '180px' }}
                            type="text"
                            placeholder="Nouveau mot de passe"
                            value={nouveauMdp[d.user_id] || ''}
                            onChange={e => setNouveauMdp(prev => ({ ...prev, [d.user_id]: e.target.value }))}
                          />
                        )}
                      </td>
                      <td style={s.td}>
                        {!d.traitee && (
                          <button
                            style={s.btnSm('#0369A1')}
                            onClick={() => traiterDemande(d.user_id, `${d.nom} ${d.prenom || ''}`)}
                            disabled={loadingMdp[d.user_id]}
                          >
                            {loadingMdp[d.user_id] ? '...' : '🔑 Valider'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* ─── Onglet Logs de sécurité ─── */}
      {onglet === 'logs' && (
        <div style={{ ...s.card, borderRadius: '0 10px 10px 10px', marginTop: '0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ color: '#6B7280', fontSize: '13px' }}>{logsFiltres.length} événement(s)</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                style={{ padding: '6px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', width: '240px' }}
                placeholder="Filtrer par action, utilisateur..."
                value={filtreLog}
                onChange={e => setFiltreLog(e.target.value)}
              />
              <button style={s.btnSm()} onClick={chargerLogs}>🔄 Actualiser</button>
            </div>
          </div>
          {logs.length === 0 ? (
            <p style={{ color: '#6B7280', textAlign: 'center', padding: '32px 0' }}>Aucun log enregistré</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Date / Heure', 'Action', 'Auteur', 'Cible', 'Détails'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logsFiltres.map((l, i) => {
                  const info = LABELS_ACTION[l.action] || { label: l.action, color: '#374151', bg: '#F3F4F6' };
                  return (
                    <tr key={l.id} style={{ background: i % 2 === 0 ? 'white' : '#F9FAFB' }}>
                      <td style={{ ...s.td, fontSize: '12px', color: '#6B7280', whiteSpace: 'nowrap' }}>
                        {new Date(l.timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}<br />
                        <span style={{ color: '#9CA3AF' }}>{new Date(l.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                      </td>
                      <td style={s.td}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: info.bg, color: info.color }}>
                          {info.label}
                        </span>
                      </td>
                      <td style={{ ...s.td, fontSize: '13px' }}>{l.auteur || '—'}</td>
                      <td style={{ ...s.td, fontSize: '13px', color: '#6B7280' }}>{l.cible || '—'}</td>
                      <td style={{ ...s.td, fontSize: '12px', color: '#6B7280', fontStyle: l.details ? 'normal' : 'italic' }}>
                        {l.details || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ─── Onglet Paramètres ─── */}
      {onglet === 'parametres' && (
        <div style={{ ...s.card, borderRadius: '0 10px 10px 10px', marginTop: '0' }}>
          <h4 style={{ color: VIOLET, margin: '0 0 4px 0' }}>📄 Facturation électronique</h4>
          <p style={{ color: '#6B7280', fontSize: '13px', marginTop: '4px', marginBottom: '16px' }}>
            Réforme facturation électronique — l'émission (facture) devient obligatoirement électronique pour Benoit
            au 1ᵉʳ septembre 2027. Le SIRET est déjà demandé sur les fiches clients professionnelles ; cette bascule
            décide seulement s'il devient une condition bloquante pour émettre une facture.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px 18px' }}>
            <div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#1F2937' }}>SIRET obligatoire pour facturer un client professionnel</div>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                {siretObligatoire
                  ? '🔒 Activé — émettre une facture à un pro sans SIRET renseigné est refusé.'
                  : '🔓 Désactivé (par défaut) — le SIRET reste demandé mais non bloquant.'}
              </div>
            </div>
            <button style={s.btn(siretObligatoire ? '#DC2626' : '#16A34A')} onClick={toggleSiretObligatoire} disabled={loadingParametre}>
              {loadingParametre ? '...' : siretObligatoire ? '🔓 Désactiver' : '🔒 Activer'}
            </button>
          </div>

          <h4 style={{ color: VIOLET, margin: '28px 0 4px 0' }}>📄 Conditions générales de vente</h4>
          <p style={{ color: '#6B7280', fontSize: '13px', marginTop: '4px', marginBottom: '16px' }}>
            Historique des versions. Une seule version est <strong>active</strong> à la fois — c'est elle qui est
            rattachée à chaque nouveau devis créé. Une version déjà activée devient définitive (elle ne peut plus
            être modifiée ni supprimée) : c'est la preuve de ce qui s'appliquait à un devis donné, même après
            l'adoption d'une version suivante.
          </p>

          {!cgvVersions.some(v => v.est_active) && (
            <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13.5px', color: '#92400E' }}>
              ⚠️ <strong>Aucune version de CGV n'est active</strong> — les devis créés actuellement ne sont
              rattachés à aucune CGV. Activez une version ci-dessous dès qu'elle est prête (une fois activée,
              une version ne peut plus être désactivée qu'en la remplaçant par une nouvelle).
            </div>
          )}

          {cgvForm === null && <button style={{...s.btn(VIOLET), marginBottom: '16px'}} onClick={ouvrirNouvelleCgv}>+ Nouvelle version</button>}

          {cgvForm !== null && (
            <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
              <label style={s.label}>Label</label>
              <input style={s.input} value={cgvForm.label} onChange={e => setCgvForm({...cgvForm, label: e.target.value})} placeholder="Ex: V1, V2 - ajout médiateur..." />
              <label style={s.label}>Contenu</label>
              <textarea style={{...s.input, minHeight: '260px', fontFamily: 'monospace', fontSize: '13px', resize: 'vertical'}} value={cgvForm.contenu} onChange={e => setCgvForm({...cgvForm, contenu: e.target.value})} placeholder="Texte complet des CGV..." />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button style={s.btn(VIOLET)} onClick={enregistrerCgv} disabled={savingCgv}>{savingCgv ? 'Enregistrement...' : '✅ Enregistrer'}</button>
                <button style={s.btn('#6B7280')} onClick={annulerCgv}>Annuler</button>
              </div>
            </div>
          )}

          {cgvVoir !== null && (
            <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <strong>{cgvVoir.label}</strong>
                <button style={s.btnSm()} onClick={() => setCgvVoir(null)}>✕ Fermer</button>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151', maxHeight: '400px', overflow: 'auto' }}>{cgvVoir.contenu}</div>
            </div>
          )}

          {cgvVersions.length === 0 ? (
            <p style={{ color: '#9CA3AF', fontSize: '14px' }}>Aucune version enregistrée</p>
          ) : (() => {
            const versionsActivees = [...cgvVersions].filter(x => x.date_activation).sort((a, b) => new Date(a.date_activation) - new Date(b.date_activation));
            const finValidite = (v) => {
              if (!v.date_activation) return undefined; // jamais activée
              if (v.est_active) return null; // en cours, pas de fin
              const idx = versionsActivees.findIndex(x => x.id === v.id);
              const suivante = versionsActivees[idx + 1];
              // Archivée sans successeur direct (ex: retrait manuel, historique) : au mieux,
              // dernière modification connue plutôt que « en cours » (faux pour une non-active).
              return suivante ? suivante.date_activation : v.updated_at;
            };
            return (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Label', 'Statut', 'Créée le', 'Période de validité', 'Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {cgvVersions.map((v, i) => (
                  <tr key={v.id} style={{ background: i % 2 === 0 ? 'white' : '#F9FAFB' }}>
                    <td style={{ ...s.td, fontWeight: '600' }}>{v.label}</td>
                    <td style={s.td}>
                      {v.est_active
                        ? <span style={s.badge(true)}>🟢 Active</span>
                        : v.date_activation
                          ? <span style={{ ...s.badge(false), background: '#F3F4F6', color: '#6B7280' }}>⚫ Archivée</span>
                          : <span style={{ ...s.badge(false), background: '#FEF3C7', color: '#D97706' }}>⚪ Brouillon</span>
                      }
                    </td>
                    <td style={s.td}>{new Date(v.created_at).toLocaleDateString('fr-FR')}</td>
                    <td style={s.td}>
                      {v.date_activation
                        ? <>Du {new Date(v.date_activation).toLocaleDateString('fr-FR')} {(() => { const fin = finValidite(v); return fin ? `au ${new Date(fin).toLocaleDateString('fr-FR')}` : '(en cours)'; })()}</>
                        : '—'
                      }
                    </td>
                    <td style={s.td}>
                      <button style={s.btnSm()} onClick={() => setCgvVoir(v)}>👁️ Voir</button>
                      {!v.date_activation && (
                        <>
                          <button style={s.btnSm('#6B7280')} onClick={() => ouvrirEditionCgv(v)}>✏️</button>
                          <button style={s.btnSm('#16A34A')} onClick={() => activerCgv(v)}>🚀 Activer</button>
                          <button style={s.btnSm('#DC2626')} onClick={() => supprimerCgv(v)}>🗑️</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            );
          })()}
        </div>
      )}
    </div>
  );
}
